import { scrubUrl, scrubValue } from "./scrub";

export type BreadcrumbKind =
  | "nav"
  | "click"
  | "network"
  | "console"
  | "cart"
  | "checkout"
  | "auth"
  | "vitals"
  | "lifecycle"
  | "info";

export interface Breadcrumb {
  t: number; // ms since page load
  ts: string; // ISO
  kind: BreadcrumbKind;
  msg: string;
  data?: Record<string, unknown>;
}

const MAX = 40;
const ring: Breadcrumb[] = [];
const t0 = typeof performance !== "undefined" ? performance.now() : 0;

// URLs we never want to breadcrumb (self-report endpoint)
const IGNORED_URL_SUBSTR = ["/api/public/client-errors", "/_serverFn/", "/__l5e/"];

// React devtools / HMR / router noise we don't want in the ring
const IGNORED_CONSOLE_PREFIXES = [
  "Warning:",
  "[HMR]",
  "[vite]",
  "[TanStack Router]",
  "Download the React DevTools",
];

function shouldIgnoreConsole(msg: string): boolean {
  return IGNORED_CONSOLE_PREFIXES.some((p) => msg.startsWith(p));
}

function shouldIgnoreUrl(url: string): boolean {
  return IGNORED_URL_SUBSTR.some((s) => url.includes(s));
}

export function addBreadcrumb(kind: BreadcrumbKind, msg: string, data?: Record<string, unknown>) {
  const b: Breadcrumb = {
    t: Math.round((typeof performance !== "undefined" ? performance.now() : 0) - t0),
    ts: new Date().toISOString(),
    kind,
    msg: String(msg).slice(0, 300),
    data: data ? (scrubValue(data) as Record<string, unknown>) : undefined,
  };
  ring.push(b);
  if (ring.length > MAX) ring.shift();
}

export function snapshotBreadcrumbs(): Breadcrumb[] {
  return ring.slice(-MAX);
}

export function installBreadcrumbAutoCapture() {
  if (typeof window === "undefined") return;
  if ((window as unknown as { __crumbsInstalled?: boolean }).__crumbsInstalled) return;
  (window as unknown as { __crumbsInstalled?: boolean }).__crumbsInstalled = true;

  // clicks with accessible name
  window.addEventListener(
    "click",
    (e) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const el =
        (target.closest("button, a, [role=button], [data-track]") as HTMLElement | null) ?? target;
      const name =
        el.getAttribute("aria-label") ||
        el.getAttribute("data-track") ||
        (el.textContent ?? "").trim().slice(0, 80);
      if (!name) return;
      addBreadcrumb("click", name, {
        tag: el.tagName.toLowerCase(),
        href: el instanceof HTMLAnchorElement ? scrubUrl(el.getAttribute("href") ?? "") : undefined,
      });
    },
    { capture: true, passive: true },
  );

  // Browser back/forward navigations. Avoid monkey-patching history methods:
  // it adds work to the hydration path and can pollute performance traces.
  window.addEventListener("popstate", () => {
    addBreadcrumb("nav", "popstate", { to: scrubUrl(location.href) });
  });

  // fetch instrumentation — skip our own reporting endpoint to avoid recursion
  const origFetch = window.fetch;
  window.fetch = async function (input: RequestInfo | URL, init?: RequestInit) {
    const started = performance.now();
    const method = (init?.method || (input instanceof Request ? input.method : "GET")).toUpperCase();
    const url =
      typeof input === "string" ? input : input instanceof URL ? input.href : (input as Request).url;
    if (shouldIgnoreUrl(url)) {
      return origFetch.call(this, input as RequestInfo, init);
    }
    try {
      const res = await origFetch.call(this, input as RequestInfo, init);
      const dur = Math.round(performance.now() - started);
      addBreadcrumb("network", `${method} ${scrubUrl(url)}`, {
        status: res.status,
        dur_ms: dur,
      });
      return res;
    } catch (err) {
      addBreadcrumb("network", `${method} ${scrubUrl(url)} FAILED`, {
        error: (err as Error)?.message,
      });
      throw err;
    }
  };

  // console.error / warn — filter framework noise
  const origErr = console.error;
  console.error = function (...args: unknown[]) {
    const joined = args
      .map((a) => (a instanceof Error ? a.message : String(a)))
      .join(" ")
      .slice(0, 300);
    if (!shouldIgnoreConsole(joined)) addBreadcrumb("console", joined);
    return origErr.apply(this, args);
  };
  const origWarn = console.warn;
  console.warn = function (...args: unknown[]) {
    const joined = "warn: " + args.map((a) => String(a)).join(" ").slice(0, 300);
    if (!shouldIgnoreConsole(joined.slice(6))) addBreadcrumb("console", joined);
    return origWarn.apply(this, args);
  };

  // lifecycle: online/offline, visibility, focus
  window.addEventListener("online", () => addBreadcrumb("lifecycle", "online"));
  window.addEventListener("offline", () => addBreadcrumb("lifecycle", "offline"));
  document.addEventListener("visibilitychange", () =>
    addBreadcrumb("lifecycle", `visibility:${document.visibilityState}`),
  );
}
